
// Attribute on each bookable flight-card wrapper; value = the offer uniqueId.
export const FLIGHT_ANCHOR_ATTR = "data-flight-anchor";
// Attribute on the bookable results section — the no-match fallback target.
export const FLIGHT_SECTION_ATTR = "data-flight-section";

// Options for scrollToBookableFlight.
//  - behavior: passed to scrollIntoView (default "smooth"). The scroll-restore
//    path passes "auto" so its repeated re-applies (image-decode settle) don't
//    visibly animate.
//  - sectionFallback: when true (default) and no per-offer card matches, scroll
//    to the results section so the streamed-preview click still lands the user
//    somewhere. The scroll-restore path passes false — a stale/mismatched anchor
//    must NOT yank the user to an unrelated section; it should simply no-op.
export interface ScrollToBookableOptions {
  behavior?: ScrollBehavior;
  sectionFallback?: boolean;
}

// Scroll the page to the bookable card for `uniqueId` (exact id match). If no
// card matches — e.g. the BE didn't echo the same id in the stream and the
// results — fall back to the results section so the click still lands the user
// in the flight list rather than doing nothing. Returns true if it scrolled.
export function scrollToBookableFlight(
  uniqueId?: string | null,
  opts?: ScrollToBookableOptions
): boolean {
  const behavior = opts?.behavior ?? "smooth";
  const sectionFallback = opts?.sectionFallback ?? true;
  const id = (uniqueId ?? "").trim();

  let target: HTMLElement | null = null;
  if (id) {
    // CSS.escape guards ids with special chars; degrade to the raw id if absent.
    const safe = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id;
    target = document.querySelector<HTMLElement>(`[${FLIGHT_ANCHOR_ATTR}="${safe}"]`);
  }

  // No per-offer match → land in the results section anyway (unless the caller
  // opted out, e.g. the scroll-restore path).
  const el =
    target ??
    (sectionFallback ? document.querySelector<HTMLElement>(`[${FLIGHT_SECTION_ATTR}]`) : null) ??
    null;
  if (!el) return false;

  // Repo idiom (CityTabs.tsx, hotelAnchor.ts) — scrollIntoView walks up to the
  // real scroll ancestor, covering the iOS body-scroll quirk noted in CLAUDE.md.
  el.scrollIntoView({ behavior, block: target ? "center" : "start" });
  return true;
}
